import {
  CUSTOMER_PERIODS,
  CUSTOMER_PROFILES,
  CUSTOMER_SEGMENTS,
  CUSTOMER_SORTS,
  DEFAULT_CUSTOMER_PERIOD,
  SORT_DIRS,
  type CustomerPeriod,
  type CustomerProfile,
  type CustomerSegment,
  type CustomerSort,
  type SortDir,
} from '@gozoki/api-types'
import { formatEurosWithCents, formatInteger } from '@gozoki/tools'
import type { SortingState } from '@tanstack/react-table'
import { z } from 'zod'

/**
 * Filtres de l'écran Clients (`/users/app`), portés par le `search` de la route : un lien partagé
 * rouvre la même vue (segment, boutique, période, tri, page). Tout ici est pur et testé sans
 * navigateur (`customers-search.test.mjs`). Périodes, segments, profils et tris sont ceux du
 * contrat `GET /customers`, partagés avec le backend par `@gozoki/api-types`.
 */

export const DEFAULT_CUSTOMER_SORT: { sortBy: CustomerSort; sortDir: SortDir } = {
  sortBy: 'amount',
  sortDir: 'desc',
}

export const PAGE_SIZES = [25, 50, 100] as const
export type PageSize = (typeof PAGE_SIZES)[number]
export const DEFAULT_PAGE_SIZE: PageSize = 25

/** Un paramètre invalide dans l'URL est ignoré plutôt que de casser l'écran (`catch`). */
export const optionalCaught = <T extends z.ZodTypeAny>(schema: T) =>
  schema.optional().catch(undefined)

/** `page` et `pageSize` : communs aux deux écrans paginés côté serveur. */
export const pageSchema = optionalCaught(z.number().int().min(1))
export const pageSizeSchema = optionalCaught(z.literal(PAGE_SIZES))

export const customersSearchSchema = z.object({
  q: optionalCaught(z.string()),
  segment: optionalCaught(z.enum(CUSTOMER_SEGMENTS)),
  profile: optionalCaught(z.enum(CUSTOMER_PROFILES)),
  storeId: optionalCaught(z.number().int().positive()),
  period: optionalCaught(z.literal(CUSTOMER_PERIODS)),
  minAmount: optionalCaught(z.number().min(0)),
  sortBy: optionalCaught(z.enum(CUSTOMER_SORTS)),
  sortDir: optionalCaught(z.enum(SORT_DIRS)),
  page: pageSchema,
  pageSize: pageSizeSchema,
})

export type CustomersSearch = z.infer<typeof customersSearchSchema>

export interface CustomersListQuery {
  page: number
  pageSize: number
  search?: string
  segment?: CustomerSegment
  profile?: CustomerProfile
  storeId?: number
  period: CustomerPeriod
  minAmount?: number
  sortBy: CustomerSort
  sortDir: SortDir
}

/**
 * Paramètres de `GET /customers`. Le profil « sans carte » relève de la solvabilité : sans le
 * droit, il n'est pas envoyé (le serveur le refuserait en 403).
 */
export const searchToCustomersQuery = (
  search: CustomersSearch,
  canReadSolvency: boolean,
): CustomersListQuery => {
  const q = search.q?.trim()
  return {
    page: search.page ?? 1,
    pageSize: search.pageSize ?? DEFAULT_PAGE_SIZE,
    ...(q ? { search: q } : {}),
    ...(search.segment === undefined ? {} : { segment: search.segment }),
    ...(search.profile === undefined || (search.profile === 'no_card' && !canReadSolvency)
      ? {}
      : { profile: search.profile }),
    ...(search.storeId === undefined ? {} : { storeId: search.storeId }),
    period: search.period ?? DEFAULT_CUSTOMER_PERIOD,
    ...(search.minAmount === undefined ? {} : { minAmount: search.minAmount }),
    sortBy: search.sortBy ?? DEFAULT_CUSTOMER_SORT.sortBy,
    sortDir: search.sortDir ?? DEFAULT_CUSTOMER_SORT.sortDir,
  }
}

/** Un changement de filtre ramène toujours à la première page. */
export const applyCustomersFilter = (
  search: CustomersSearch,
  change: Partial<CustomersSearch>,
): CustomersSearch => ({ ...search, ...change, page: undefined })

/** Clic sur une tuile segment : active le segment, un second clic le retire. */
export const toggleSegment = (search: CustomersSearch, segment: CustomerSegment): CustomersSearch =>
  applyCustomersFilter(search, { segment: search.segment === segment ? undefined : segment })

/** Clic sur la tuile « Nouveaux comptes » : même logique de bascule que les segments. */
export const toggleProfile = (search: CustomersSearch, profile: CustomerProfile): CustomersSearch =>
  applyCustomersFilter(search, { profile: search.profile === profile ? undefined : profile })

/** Les filtres en plus de la recherche et de la période (pour le bouton « Réinitialiser »). */
export const hasActiveCustomersFilters = (search: CustomersSearch): boolean =>
  search.segment !== undefined ||
  search.profile !== undefined ||
  search.storeId !== undefined ||
  search.minAmount !== undefined ||
  (search.q?.trim().length ?? 0) > 0

export const clearCustomersFilters = (search: CustomersSearch): CustomersSearch => ({
  period: search.period,
  pageSize: search.pageSize,
  sortBy: search.sortBy,
  sortDir: search.sortDir,
})

/**
 * Champ « montant minimum » (CA, dû) saisi librement : vide, non numérique ou nul → pas de
 * filtre ; la virgule décimale française est acceptée.
 */
export const parseMinAmountInput = (raw: string): number | undefined => {
  const trimmed = raw.trim()
  if (trimmed === '') return undefined
  const parsed = Number(trimmed.replace(',', '.'))
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined
}

/** État de tri courant, tel que l'entend TanStack Table (`{ id, desc }`). */
export const sortingFromSearch = (search: {
  sortBy?: CustomerSort | string
  sortDir?: SortDir
}): { id: string; desc: boolean } => ({
  id: search.sortBy ?? DEFAULT_CUSTOMER_SORT.sortBy,
  desc: (search.sortDir ?? DEFAULT_CUSTOMER_SORT.sortDir) === 'desc',
})

/**
 * Traduit un changement de tri TanStack Table (`onSortingChange`, en tri serveur `manualSorting`)
 * en paramètres `sortBy` / `sortDir` ; `undefined` quand la colonne n'a pas de tri serveur
 * (`sortable` associe l'id de colonne à la clé de tri de l'API).
 */
export const resolveServerSort = <S extends string>(
  updater: SortingState | ((previous: SortingState) => SortingState),
  current: SortingState,
  sortable: Readonly<Record<string, S>>,
): { sortBy: S; sortDir: SortDir } | undefined => {
  const next = typeof updater === 'function' ? updater(current) : updater
  const first = next[0]
  if (first === undefined) return undefined
  const sortBy = sortable[first.id]
  if (sortBy === undefined) return undefined
  return { sortBy, sortDir: first.desc ? 'desc' : 'asc' }
}

/**
 * « 1 à 25 sur 53 927 clients », « Aucun client », ou « Aucun client sur cette page » (page hors
 * plage dans l'URL) : pied de tableau.
 */
export const pageRangeLabel = (
  total: number,
  page: number,
  pageSize: number,
  count: number,
  noun = 'client',
): string => {
  if (total === 0) return `Aucun ${noun}`
  if (count === 0) return `Aucun ${noun} sur cette page`
  const first = (Math.max(1, page) - 1) * pageSize + 1
  const last = first + count - 1
  const plural = total > 1 ? 's' : ''
  return `${first} à ${last} sur ${formatInteger(total)} ${noun}${plural}`
}

export const totalPages = (total: number, pageSize: number): number =>
  Math.max(1, Math.ceil(total / pageSize))

/**
 * Résumé d'une sélection : « 3 clients sélectionnés · 71,40 € dus » (le montant seulement quand
 * il est connu, c'est-à-dire avec la solvabilité sur l'écran Clients bannis).
 */
export const selectionSummary = (count: number, amountDue: number | null): string => {
  const head = `${formatInteger(count)} client${count > 1 ? 's' : ''} sélectionné${count > 1 ? 's' : ''}`
  if (amountDue === null) return head
  return `${head} · ${formatEurosWithCents(amountDue)} dus`
}

const CUSTOMER_SORT_LABELS: Record<CustomerSort, (period: CustomerPeriod) => string> = {
  amount: (period) => `CA ${period} j`,
  carts: (period) => `paniers ${period} j`,
  lastCart: () => 'dernier achat',
  lastEntry: () => 'dernière entrée',
  created: () => 'date de création',
}

/** « tri : CA 90 j décroissant » */
export const sortLabel = (sortBy: CustomerSort, sortDir: SortDir, period: CustomerPeriod): string =>
  `tri : ${CUSTOMER_SORT_LABELS[sortBy](period)} ${sortDir === 'desc' ? 'décroissant' : 'croissant'}`
